// sessionsStore.ts
import { create } from "zustand";
import type { CustomAlertProps } from "../../components/base/CustomAlert";
import { getError } from "../../utils/getError";
import type { TFunction } from "i18next";
import api, { isAbortError } from "../../utils/api";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";

export interface SessionLocation {
  city: string;
  region: string;
  country: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  timezone: string;
}

export interface Session {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  role: string;
  device: string;
  platform: string;
  userAgent: string;
  ip: string;
  location: SessionLocation | string;
  active: boolean;
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt: { _seconds: number; _nanoseconds: number };
  endedAt: { _seconds: number; _nanoseconds: number } | null;
  duration?: {
    hours: number;
    minutes: number;
    formatted: string;
    totalMinutes: number;
  };
}

interface SessionsMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

interface SessionsStore {
  sessions: Session[];
  loading: boolean;
  error: string | null;
  meta?: SessionsMeta;
  stats: {
    total: number;
    active: number;
    inactive: number;
  };
  fetchSessions: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
    userId?: string;
    active?: string;
    platform?: string;
    search?: string;
  }) => Promise<void>;
  setSessions: (sessions: Session[], meta?: SessionsMeta) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setStats: (stats: {
    total: number;
    active: number;
    inactive: number;
  }) => void;
  updateSessionInStore: (
    sessionId: string,
    updatedSession: Partial<Session>
  ) => void;
  removeSessionFromStore: (sessionId: string) => void;
}

export const useSessionsStore = create<SessionsStore>((set, get) => ({
  sessions: [],
  loading: false,
  error: null,
  meta: undefined,
  stats: {
    total: 0,
    active: 0,
    inactive: 0,
  },

  fetchSessions: async (params = {}) => {
    if (get().loading) return;
    const fetchGen = getAdminFetchGeneration();
    set({ loading: true, error: null });

    try {
      const { data } = await api.get("/sessionList", {
        params: {
          limit: params.limit ?? 6,
          page: params.page ?? 1,
          ...params,
        },
      });

      if (getAdminFetchGeneration() !== fetchGen) return;
      
      // Validar y corregir estado de sesiones
      const validatedSessions = (data.data || []).map((session: Session) => {
        // Si la sesión está marcada como activa pero tiene endedAt, corregir
        if (session.active && session.endedAt) {
          return {
            ...session,
            active: false, // Corregir: si tiene endedAt no puede estar activa
          };
        }
        return session;
      });

      const total = data.meta?.totalItems || validatedSessions.length || 0;
      const active = validatedSessions.filter((s: Session) => s.active).length || 0;
      const inactive = total - active;

      set({
        sessions: validatedSessions,
        meta: data.meta,
        stats: { total, active, inactive },
        loading: false,
      });
    } catch (err: any) {
      if (getAdminFetchGeneration() !== fetchGen || isAbortError(err)) return;
      set({ error: getError(err), loading: false });
    }
  },

  setSessions: (sessions: Session[], meta) => {
    set({ sessions, meta });
  },

  setLoading: (loading: boolean) => set({ loading }),

  setError: (error: string | null) => set({ error }),

  setStats: (stats) => set({ stats }),

  updateSessionInStore: (
    sessionId: string,
    updatedSession: Partial<Session>
  ) => {
    set((state) => ({
      sessions: state.sessions.map((session) =>
        session.id === sessionId ? { ...session, ...updatedSession } : session
      ),
    }));
  },

  removeSessionFromStore: (sessionId: string) => {
    set((state) => ({
      sessions: state.sessions.filter((session) => session.id !== sessionId),
    }));
  },
}));

export const getSessions = async (
  params: {
    limit?: number;
    page?: number;
    offset?: number;
    userId?: string;
    active?: string;
    platform?: string;
    search?: string;
  },
  t: TFunction<"translation", undefined>,
  showAlert: (config: CustomAlertProps) => void
) => {
  const store = useSessionsStore.getState();

  if (store.loading) return;

  store.setLoading(true);
  store.setError(null);

  try {
    const { data } = await api.get("/sessionList", { params });

    if (data.data) {
      // Validar y corregir estado de sesiones
      const validatedSessions = data.data.map((session: Session) => {
        // Si la sesión está marcada como activa pero tiene endedAt, corregir
        if (session.active && session.endedAt) {
          return {
            ...session,
            active: false, // Corregir: si tiene endedAt no puede estar activa
          };
        }
        return session;
      });

      store.setSessions(validatedSessions, data.meta);

      // Calcular stats
      const total = data.meta?.totalItems || validatedSessions.length;
      const active = validatedSessions.filter((s: Session) => s.active).length;
      const inactive = total - active;
      store.setStats({ total, active, inactive });
    }
  } catch (error) {
    const errorMessage = getError(error, t);

    store.setError(errorMessage);

    showAlert({
      title: t("sessions.form.errorMessage"),
      text: errorMessage,
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  } finally {
    store.setLoading(false);
  }
};

export const closeSession = async (
  sessionId: string,
  showAlert: (config: CustomAlertProps) => void,
  t: TFunction<"translation", undefined>,
  onSuccess?: () => void
) => {
  try {
    // Enviar tanto 'id' (para el controller) como 'sessionId' (para el middleware)
    await api.put(
      "/session/close",
      { active: false },
      { params: { id: sessionId, sessionId: sessionId } }
    );

    useSessionsStore.getState().updateSessionInStore(sessionId, {
      active: false,
      endedAt: { _seconds: Math.floor(Date.now() / 1000), _nanoseconds: 0 },
    });

    showAlert({
      title: t("sessions.closeSuccess"),
      text: t("sessions.closeSuccessMessage"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onSuccess?.();
      },
    });
  } catch (error) {
    showAlert({
      title: t("sessions.closeError"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

// Cerrar todas las sesiones de un usuario
export const closeAllUserSessions = async (
  userId: string,
  exceptSessionId: string | null,
  showAlert: (config: CustomAlertProps) => void,
  t: TFunction<"translation", undefined>,
  onSuccess?: () => void
) => {
  try {
    const { data } = await api.put("/session/closeAll", null, {
      params: { userId, exceptSessionId: exceptSessionId || undefined },
    });

    showAlert({
      title: t("sessions.closeAllSuccess"),
      text: t("sessions.closeAllSuccessMessage", {
        count: data.data?.closedCount || 0,
      }),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onSuccess?.();
      },
    });
  } catch (error) {
    showAlert({
      title: t("sessions.closeAllError"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

// ============================================
// API TOKENS
// ============================================

export interface ApiToken {
  data: {
    token: string;
    tokenId: string;
    name: string;
    scopes: string[];
    expirationDate: string | null;
    warning: string;
  };
}

export interface GenerateTokenParams {
  name?: string;
  scopes?: string[];
  expiresInDays?: number;
}

// Generar API Token
export const generateApiToken = async (
  params: GenerateTokenParams,
  t: TFunction<"translation", undefined>,
  showAlert: (config: CustomAlertProps) => void
): Promise<ApiToken | null> => {
  try {
    const { data } = await api.post("/session/apiToken", {
      name: params.name || "API Token",
      scopes: params.scopes || ["read", "write"],
      expiresInDays: params.expiresInDays ?? 0,
    });

    return data.data as ApiToken;
  } catch (error) {
    showAlert({
      title: t("sessions.apiToken.errorTitle"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
    return null;
  }
};

// Helper para formatear ubicación
export const formatLocation = (location: SessionLocation | string): string => {
  if (!location) return "Unknown";
  if (typeof location === "string") return location;

  const city = location.city || "";
  const country = location.country || "";

  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  if (country) return country;
  return "Unknown";
};

// Helper para obtener color de estado - FUNCIÓN CONSOLIDADA
export const getStatusInfo = (
  active: boolean,
  endedAt: { _seconds: number; _nanoseconds: number } | null,
  t?: TFunction<"translation", undefined>
): { bg: string; text: string; label: string } => {
  if (active) {
    return { 
      bg: "bg-green-100", 
      text: "text-green-600", 
      label: t ? t("sessions.status.active") : "Activa" 
    };
  }
  
  if (endedAt) {
    const endedTime = endedAt._seconds ? endedAt._seconds * 1000 : 0;
    const hoursAgo = (Date.now() - endedTime) / (1000 * 60 * 60);
    if (hoursAgo > 24) {
      return { 
        bg: "bg-red-100", 
        text: "text-red-600", 
        label: t ? t("sessions.status.expired") : "Expirada" 
      };
    }
  }
  
  return { 
    bg: "bg-gray-100", 
    text: "text-gray-600", 
    label: t ? t("sessions.status.inactive") : "Inactiva" 
  };
};

// Helper para obtener color de estado (deprecated - usar getStatusInfo)
export const getStatusColor = (
  active: boolean,
  endedAt: any
): { bg: string; text: string; label: string } => {
  return getStatusInfo(active, endedAt);
};

// Helper para obtener color de plataforma
export const getPlatformColor = (
  platform: string
): { bg: string; text: string } => {
  switch (platform?.toLowerCase()) {
    case "ios app":
      return { bg: "bg-blue-100", text: "text-blue-600" };
    case "android app":
      return { bg: "bg-green-100", text: "text-green-600" };
    case "web":
    case "web dashboard":
      return { bg: "bg-purple-100", text: "text-purple-600" };
    default:
      return { bg: "bg-gray-100", text: "text-gray-600" };
  }
};

// Helper para obtener color de rol
export const getRoleColor = (role: string): { bg: string; text: string } => {
  switch (role?.toLowerCase()) {
    case "admin":
    case "administrador":
      return { bg: "bg-purple-100", text: "text-purple-600" };
    case "driver":
    case "conductor":
      return { bg: "bg-blue-100", text: "text-blue-600" };
    case "supervisor":
      return { bg: "bg-orange-100", text: "text-orange-600" };
    case "monitor":
      return { bg: "bg-cyan-100", text: "text-cyan-600" };
    default:
      return { bg: "bg-gray-100", text: "text-gray-600" };
  }
};
